import { CheckOutlined, ContentCopy, HelpOutline } from '@mui/icons-material';
import {
	Box,
	ClickAwayListener,
	IconButton,
	Link,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

interface SupportEmailTooltipProps {
	supportEmail: string;
}

const SupportEmailTooltip: React.FC<SupportEmailTooltipProps> = ({
	supportEmail
}) => {
	const [open, setOpen] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const [, copy] = useCopyToClipboard();

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Box>
				<Tooltip
					open={open}
					disableHoverListener
					disableFocusListener
					disableTouchListener
					arrow
					title={
						<Stack
							direction="row"
							alignItems="center"
							gap={0.5}
							flexWrap="wrap"
						>
							<Typography variant="body1">
								{intl.tr('app.navbar.supportEmailTooltip')}
							</Typography>
							<Link
								href={`mailto:${supportEmail.trim().replace(/[?&#\n\r]/g, '')}`}
								variant="body1"
								color="inherit"
								underline="always"
								data-testid="topbar-support-email-link"
							>
								{supportEmail}
							</Link>
							<IconButton
								size="small"
								sx={{ color: 'inherit' }}
								onClick={() => {
									copy(supportEmail.trim());
									setIsCopied(true);
								}}
								data-testid="topbar-support-email-copy"
								aria-label="Copy support email"
							>
								{isCopied ? (
									<CheckOutlined
										fontSize="small"
										data-testid="topbar-support-email-copied"
									/>
								) : (
									<ContentCopy fontSize="small" />
								)}
							</IconButton>
						</Stack>
					}
					slotProps={{
						popper: {
							modifiers: [
								{
									name: 'preventOverflow',
									options: { padding: 16 } // prevents the tooltip from reaching the edge of the screen
								}
							]
						},
						tooltip: { sx: { maxWidth: 'min(90vw, 450px)' } } // prevents tooltip text wrap
					}}
				>
					<IconButton
						size="small"
						onClick={() => {
							setOpen((prev) => !prev);
							setIsCopied(false);
						}}
						data-testid="topbar-support-email-icon"
						aria-label="Support email info"
					>
						<HelpOutline color="action" fontSize="medium" />
					</IconButton>
				</Tooltip>
			</Box>
		</ClickAwayListener>
	);
};

export default SupportEmailTooltip;
